import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { scheduledBroadcastsKey } from '../../../lib/groupQueryKeys';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { formatInZone, resolveTripTimeZone, wallClockToIso } from '../../../lib/tripTime';
import {
  AUDIENCE_KIND_FALLBACK_NOTE,
  audienceKindFor,
  scheduleWithAudienceFallback,
} from '../../../lib/audienceKind';
import {
  buildScheduleRequest,
  isScheduledBroadcastsUnavailable,
  scheduleEmptyAudienceMessage,
  sendAtIsInFuture,
} from '../../../lib/scheduledBroadcasts';
import { audienceInviteeIds } from './audience';
import { useAudienceKindSupport } from './useAudienceKindSupport';
import { useScheduledBroadcasts } from './useScheduledBroadcasts';
import type { BroadcastChannel } from '../../../lib/broadcastChannels';
import type { BroadcastRoster } from './useBroadcastRoster';
import type { GroupDetail } from '../../../types/groups';

export type SendMode = 'now' | 'later';

/**
 * The composer's one extra choice — "Send now" (untouched, handled by
 * `BroadcastsTab` itself) or "Send at…" — plus everything "Send at…" needs:
 * the trip-zone time field, the future/empty-audience guards, and the
 * schedule mutation itself.
 *
 * Deliberately does NOT re-run the interactive dry-run preview: the schedule
 * endpoint already runs its own dry-run against today's audience server-side
 * (see `ScheduleBroadcast` in `GroupsController.ScheduledBroadcasts.cs`) and
 * answers the same 400s an interactive send would. This component's client
 * guards are the FIRST line of defence (composeReady, the future check, an
 * RSVP audience that resolved to nobody) — the server 400 is the second, not
 * the only one.
 *
 * Reuses `roster` and `audienceInviteeIds` exactly as `BroadcastsTab` does for
 * "Send now" — the audience for a scheduled send must never be computed a
 * second, independently-written way.
 */
export default function ScheduleControls({
  group,
  mode,
  onModeChange,
  subject,
  body,
  smsBody,
  smsSelected,
  channels,
  segment,
  personalizeInviteLinks,
  roster,
  composeReady,
}: {
  group: GroupDetail;
  mode: SendMode;
  onModeChange: (mode: SendMode) => void;
  subject: string;
  body: string;
  smsBody: string;
  smsSelected: boolean;
  channels: BroadcastChannel[];
  segment: string;
  personalizeInviteLinks: boolean;
  roster: BroadcastRoster;
  composeReady: boolean;
}) {
  const queryClient = useQueryClient();
  // Shared react-query key with ScheduledBroadcastsList: `unavailable` reflects
  // the SAME 503 probe the list already makes, at no extra network cost.
  const { unavailable } = useScheduledBroadcasts(group.id);
  const { timeZone, isFallback: zoneIsFallback } = resolveTripTimeZone(group.timeZone);

  const [scheduleLocal, setScheduleLocal] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduledNotice, setScheduledNotice] = useState<string | null>(null);

  // Pinned to the trip's zone, never the browser's — see tripTime.ts. This is
  // the exact trap fixed elsewhere in Studio: a host in Chicago scheduling a
  // Lisbon 9am must not have it fire at Chicago 9am.
  const sendAtIso = wallClockToIso(scheduleLocal, timeZone);
  // Frozen ids: the fallback for an API that cannot decide RSVP audiences at
  // send time, and the estimate the client-side empty check reads. When the
  // server decides, an empty audience NOW is fine — a day-before reminder is
  // scheduled while nobody has said going yet, and it re-resolves when it fires.
  const inviteeIds = audienceInviteeIds(segment, roster.guests);
  const { supported: kindSupported, checked: kindChecked } = useAudienceKindSupport(group.id, segment);
  const audienceKind = kindSupported ? audienceKindFor(segment) : null;
  const [fellBack, setFellBack] = useState(false);
  const emptyAudienceMessage = audienceKind ? null : scheduleEmptyAudienceMessage(inviteeIds, roster.missing);
  const frozenAudience = audienceKindFor(segment) !== null && (fellBack || (kindChecked && !kindSupported));
  const futureOk = sendAtIsInFuture(sendAtIso);
  const canSchedule = composeReady && Boolean(sendAtIso) && futureOk
    && !emptyAudienceMessage && !unavailable;

  const scheduleMutation = useMutation(
    async () => {
      const request = (kind: string | null) => buildScheduleRequest({
        subject,
        body,
        channels,
        smsSelected,
        smsBody,
        segment,
        inviteeIds,
        audienceKind: kind,
        personalizeInviteLinks,
        sendAt: sendAtIso || '',
      });
      const { row, fellBack: usedFallback } = await scheduleWithAudienceFallback(
        (req) => groupsApi.scheduleBroadcast(group.id, req),
        request(audienceKind),
        request(null),
      );
      if (usedFallback) setFellBack(true);
      return row;
    },
    {
      onSuccess: (result) => {
        // One key, two readers: this invalidation is what makes the pending
        // list below pick up the new row without any callback wiring.
        queryClient.invalidateQueries(scheduledBroadcastsKey(group.id));
        setScheduleLocal('');
        setConfirmOpen(false);
        setScheduledNotice(formatInZone(result.sendAt, timeZone));
      },
    },
  );

  const error = scheduleMutation.error
    ? (isScheduledBroadcastsUnavailable(scheduleMutation.error)
      ? "Scheduled sends aren't available on this environment yet."
      : mutationErrorMessage(scheduleMutation.error) || 'Could not schedule that broadcast.')
    : null;

  if (unavailable) {
    // The designed 503 state: say so once, plainly, and offer nothing that
    // will only fail again — no "Send at…" option, no retry button.
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Scheduled sends aren't available on this environment yet.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {frozenAudience ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>{AUDIENCE_KIND_FALLBACK_NOTE}</Alert>
      ) : null}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, next: SendMode | null) => { if (next) onModeChange(next); }}
      >
        <ToggleButton value="now">Send now</ToggleButton>
        <ToggleButton value="later">Send at…</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'later' ? (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            label={`Send at (${timeZone})`}
            type="datetime-local"
            value={scheduleLocal}
            onChange={(e) => { setScheduleLocal(e.target.value); setScheduledNotice(null); }}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 260 }}
            helperText={zoneIsFallback ? 'This trip has no time zone set — using yours.' : 'Local time at the trip.'}
          />
          {scheduleLocal && !futureOk ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>Pick a time in the future.</Alert>
          ) : null}
          {emptyAudienceMessage ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>{emptyAudienceMessage}</Alert>
          ) : null}
          {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
          {scheduledNotice ? (
            <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setScheduledNotice(null)}>
              Scheduled for {scheduledNotice}. See the list below to review or cancel it.
            </Alert>
          ) : null}
          <Button
            variant="contained"
            sx={{ mt: 1.5, display: 'block' }}
            disabled={!canSchedule || scheduleMutation.isLoading}
            onClick={() => setConfirmOpen(true)}
          >
            {scheduleMutation.isLoading ? 'Scheduling…' : 'Schedule'}
          </Button>
        </Box>
      ) : null}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Schedule this broadcast?</DialogTitle>
        <DialogContent>
          {/* The whole point of design decision #5: the resolved time, WITH the
              zone named, in front of the host before the click — so "9:00 AM"
              cannot silently mean the wrong city. */}
          <DialogContentText>
            This will send at {sendAtIso ? formatInZone(sendAtIso, timeZone) : 'an unset time'}. You can cancel it
            any time before then from the list below.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSchedule || scheduleMutation.isLoading}
            onClick={() => {
              // The dialog can sit open past the chosen time. Re-check the
              // instant at the moment of commitment rather than trusting the
              // render that opened it; the server would 400 anyway, but the
              // host should see "that time has passed", not a failed request.
              if (!sendAtIsInFuture(sendAtIso)) {
                setConfirmOpen(false);
                return;
              }
              scheduleMutation.mutate();
            }}
          >
            {scheduleMutation.isLoading ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
